import { useState } from 'react'

import type { TemplateNode } from '@gymcloud/mixedscript'

import { CycleSentence, CycleWeeks } from '~/components/cycle-weeks'
import { Dialog } from '~/components/dialog'
import { firstDayOfWeek } from '~/lib/weekdays'

/**
 * The question standing between somebody and a run beginning. Two things can need asking and
 * either can be absent, so they are asked together rather than one dialog after another: what
 * ending the run underway costs, and — on a strict schedule — which weekday the cycle begins on.
 *
 * The weekday matters because a strict template's days are written as days of a cycle and read
 * as weekdays: a program that squats on the week's first day should squat there whichever
 * evening somebody happened to start it. See `/specs/features/workout.spec.md`, the Anchor
 * concept.
 */
export function StartRunDialog({
  name,
  ending,
  strict,
  onStart,
  onDismiss,
}: {
  name: string
  /** The run this one would end, when there is one. */
  ending: string | null
  /** The template, when its cycle needs landing on a calendar; null on any other schedule. */
  strict: TemplateNode | null
  onStart: (anchorDay: number | null) => void
  onDismiss: () => void
}) {
  // The week's own first day, so the run lines up with the weekdays the editor already shows.
  const [anchorDay, setAnchorDay] = useState(firstDayOfWeek)

  // Wide enough that a workout's name fits in a column of the week it is drawn on.
  return (
    <Dialog label={`Start ${name}?`} onDismiss={onDismiss} className="max-w-2xl">
      <h2 className="text-lg font-medium">Start {name}?</h2>
      {strict !== null ? <CycleSentence node={strict} /> : null}

      {ending !== null ? (
        <p className="text-sm text-ink-muted">
          {ending} ends and joins the history. {name} begins a fresh run, and its progressions start
          over.
        </p>
      ) : null}

      {strict !== null ? (
        <CycleWeeks
          node={strict}
          anchor={anchorDay}
          onAnchor={setAnchorDay}
          heading="The cycle begins on"
        />
      ) : null}

      <div className="flex flex-wrap justify-end gap-3">
        <button type="button" className="rounded-full px-4 py-2 text-sm" onClick={onDismiss}>
          Cancel
        </button>
        <button
          type="button"
          className="rounded-full bg-surface-bright px-4 py-2 text-sm font-medium text-danger"
          onClick={() => {
            onStart(strict === null ? null : anchorDay)
          }}
        >
          Start
        </button>
      </div>
    </Dialog>
  )
}
